import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  Tooltip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import SimpleCard from '../../../../../components/SimpleCard';
import WizardButtons from '../WizardButtons';
import {
  setVendorRetailPayload,
  checkSlotAvailability,
  getVendorRetailBookings,
} from '../../../../../core/vendor-retail/action';
import { createVendorRetailBooking } from '../../../../../core/vendor-retail/service';

export default function DateTimeSelection({ nextStep, previousStep }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const { vendorRetailPayload, bookings } = useSelector((store) => store.vendorRetail);
  const { profile } = useSelector((store) => store.retailProfiles);
  const { user } = useSelector((store) => store.users);

  // Check for preselected date from navigation state
  const preselectedDate = location.state?.preselectedDate;

  const [selectedDate, setSelectedDate] = useState(
    preselectedDate ? moment(preselectedDate) : null,
  );
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [selectedTimeSlots, setSelectedTimeSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasExistingBookings, setHasExistingBookings] = useState(false);

  const schedules = profile?.schedules || [];

  // Fetch vendor's existing bookings on mount
  useEffect(() => {
    // console.log('🔍 [DateTimeSelection] useEffect - Checking user ID:', user?.id);
    if (user?.id) {
      // console.log('📞 [DateTimeSelection] Fetching bookings for vendor ID:', user.id);
      // console.log('✅ [DateTimeSelection] Backend route fixed: Using @Override() on getOne method');
      dispatch(getVendorRetailBookings(user.id));
    } else {
      // console.warn('⚠️ [DateTimeSelection] No user ID found, skipping bookings fetch');
    }
  }, [user?.id, dispatch, user]);

  // Log when bookings are updated
  useEffect(() => {
    // console.log('📦 [DateTimeSelection] Bookings updated:', {
    //   count: bookings?.length || 0,
    //   bookings,
    // });
  }, [bookings]);

  // Get unique days of week that have schedules
  const getAvailableDaysOfWeek = () => {
    const days = new Set();
    schedules.forEach((schedule) => {
      if (schedule.day_of_week) {
        days.add(schedule.day_of_week.toLowerCase());
      }
    });
    return days;
  };

  const availableDays = getAvailableDaysOfWeek();

  // Check if a date should be disabled (no schedules for that day of week)
  const shouldDisableDate = (date) => {
    if (!date) return false;
    const dayOfWeek = date.format('dddd').toLowerCase();
    return !availableDays.has(dayOfWeek);
  };

  // Filter schedules for selected day of week
  const getSchedulesForDate = (date) => {
    if (!date) return [];
    const dayOfWeek = date.format('dddd').toLowerCase();
    return schedules.filter((s) => s.day_of_week && s.day_of_week.toLowerCase() === dayOfWeek);
  };

  // Generate hourly time slots between start and end time
  const generateTimeSlots = (startTime, endTime) => {
    const slots = [];
    const start = moment(startTime, 'HH:mm:ss');
    const end = moment(endTime, 'HH:mm:ss');

    const current = start.clone();
    while (current.isBefore(end)) {
      const slotEnd = current.clone().add(1, 'hour');
      if (slotEnd.isAfter(end)) break;

      slots.push({
        start: current.format('HH:mm:ss'),
        end: slotEnd.format('HH:mm:ss'),
        display: `${current.format('h:mm A')} - ${slotEnd.format('h:mm A')}`,
      });
      current.add(1, 'hour');
    }
    return slots;
  };

  // Check if a time slot has already been applied for
  const isSlotAlreadyApplied = (slot) => {
    if (!selectedDate || !selectedSchedule || !bookings || bookings.length === 0) {
      // console.log('🔍 [isSlotAlreadyApplied] Early return - missing data:', {
      //   hasDate: !!selectedDate,
      //   hasSchedule: !!selectedSchedule,
      //   bookingsCount: bookings?.length || 0,
      // });
      return false;
    }

    const dateStr = selectedDate.format('YYYY-MM-DD');
    // console.log('🔍 [isSlotAlreadyApplied] Checking slot:', {
    //   slot: slot.display,
    //   date: dateStr,
    //   scheduleId: selectedSchedule.id,
    //   totalBookings: bookings.length,
    // });

    // Check if there's an existing booking for this date, schedule, and time slot
    // that hasn't been cancelled
    return bookings.some((booking) => {
      if (booking.has_been_cancelled) return false;

      const bookingDate = moment(booking.booking_date).format('YYYY-MM-DD');
      const bookingStartTime = booking.start_time;
      const bookingEndTime = booking.end_time;

      // Check if this booking matches the date and schedule
      if (bookingDate !== dateStr || booking.retail_schedule_id !== selectedSchedule.id) {
        return false;
      }

      // Check if the slot overlaps with the booking time range
      // A slot overlaps if it starts before the booking ends and ends after the booking starts
      const slotStart = moment(slot.start, 'HH:mm:ss');
      const slotEnd = moment(slot.end, 'HH:mm:ss');
      const bookingStart = moment(bookingStartTime, 'HH:mm:ss');
      const bookingEnd = moment(bookingEndTime, 'HH:mm:ss');

      const overlaps = slotStart.isBefore(bookingEnd) && slotEnd.isAfter(bookingStart);

      if (overlaps) {
        // console.log('✅ [isSlotAlreadyApplied] Found matching booking!', {
        //   slotTime: slot.display,
        //   bookingTime: `${bookingStart.format('h:mm A')} - ${bookingEnd.format('h:mm A')}`,
        //   bookingId: booking.id,
        //   bookingStatus: booking.application_status,
        // });
      }

      return overlaps;
    });
  };

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
    setSelectedSchedule(null);
    setSelectedTimeSlots([]);
    setError('');
  };

  const handleScheduleSelect = (schedule) => {
    setSelectedSchedule(schedule);
    setSelectedTimeSlots([]);
    setError('');
  };

  const handleTimeSlotToggle = (slot) => {
    // Don't allow selection of already applied slots
    if (isSlotAlreadyApplied(slot)) {
      return;
    }

    setSelectedTimeSlots((prev) => {
      const exists = prev.find((s) => s.start === slot.start);
      if (exists) {
        return prev.filter((s) => s.start !== slot.start);
      }
      // Keep slots sorted
      return [...prev, slot].sort((a, b) => a.start.localeCompare(b.start));
    });
    setError('');
  };

  const calculateTotalPrice = () => {
    if (!selectedSchedule || selectedTimeSlots.length === 0) return 0;
    return selectedSchedule.price_per_hour * selectedTimeSlots.length;
  };

  const handleNext = () => {
    if (!selectedDate || !selectedSchedule || selectedTimeSlots.length === 0) {
      setError('Please select a date, schedule, and at least one time slot');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Calculate total price and duration
      const totalPrice = calculateTotalPrice();
      const totalDuration = selectedTimeSlots.length; // Each slot is 1 hour

      // Store selection in Redux payload (don't create booking yet)
      dispatch(
        setVendorRetailPayload({
          retailScheduleId: selectedSchedule.id,
          venueId: vendorRetailPayload.venueId || profile.venue_id,
          bookingDate: selectedDate.format('YYYY-MM-DD'),
          timeSlots: selectedTimeSlots,
          totalPrice,
          durationHours: totalDuration,
          pricePerHour: selectedSchedule.price_per_hour,
        }),
      );

      // Move to next step
      setTimeout(() => {
        nextStep();
        setLoading(false);
      }, 100);
    } catch (err) {
      console.error('Error processing selection:', err);
      setError('Failed to process selection. Please try again.');
      setLoading(false);
    }
  };

  const availableSchedules = getSchedulesForDate(selectedDate);
  const timeSlots = selectedSchedule
    ? generateTimeSlots(selectedSchedule.start_time, selectedSchedule.end_time)
    : [];

  // Check if any time slots have existing bookings
  useEffect(() => {
    if (selectedDate && selectedSchedule && timeSlots.length > 0) {
      const hasMatches = timeSlots.some((slot) => isSlotAlreadyApplied(slot));
      setHasExistingBookings(hasMatches);
      if (hasMatches) {
        // console.log('ℹ️ [DateTimeSelection] Found existing bookings for this date/schedule');
      }
    } else {
      setHasExistingBookings(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, selectedSchedule, timeSlots, bookings]);

  // Auto-select schedule when date is preselected from calendar
  useEffect(() => {
    if (preselectedDate && selectedDate && !selectedSchedule && schedules.length > 0) {
      const dayOfWeek = moment(preselectedDate).format('dddd').toLowerCase();
      const matchingSchedules = schedules.filter(
        (s) => s.day_of_week && s.day_of_week.toLowerCase() === dayOfWeek,
      );
      // Auto-select the first matching schedule if only one exists
      if (matchingSchedules.length === 1) {
        setSelectedSchedule(matchingSchedules[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedDate, selectedDate, schedules]);

  return (
    <div>
      <Grid container justifyContent="center" mb={2}>
        <Grid item>
          <Typography fontWeight="bold">Select Date & Time:</Typography>
        </Grid>
        <Grid item>
          <Typography marginLeft={2}>Choose your booking date and time slots</Typography>
        </Grid>
      </Grid>

      {/* Show indicator if date was prepopulated */}
      {preselectedDate && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Date Prepopulated!</strong> We&apos;ve pre-selected{' '}
          {moment(preselectedDate).format('MMMM Do, YYYY')} (
          {moment(preselectedDate).format('dddd')}) from your calendar selection. You can change it
          below if needed.
        </Alert>
      )}

      <SimpleCard variant="outlined">
        <CardContent>
          {/* Date Selection */}
          <Box mb={3}>
            <Typography fontWeight="bold" mb={1}>
              Step 1: Select Date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="Booking Date"
                value={selectedDate}
                onChange={handleDateChange}
                minDate={moment(profile?.start_date || moment())}
                maxDate={profile?.end_date ? moment(profile.end_date) : undefined}
                shouldDisableDate={shouldDisableDate}
                renderInput={(params) => <TextField {...params} fullWidth variant="outlined" />}
              />
            </LocalizationProvider>
          </Box>

          {/* Schedule Selection */}
          {selectedDate && (
            <Box mb={3}>
              <Typography fontWeight="bold" mb={1}>
                Step 2: Select Schedule ({moment(selectedDate).format('dddd')})
              </Typography>
              {availableSchedules.length === 0 ? (
                <Alert severity="info">No schedules available for this day</Alert>
              ) : (
                <Grid container spacing={2}>
                  {availableSchedules.map((schedule) => (
                    <Grid item xs={12} sm={6} key={schedule.id}>
                      <Card
                        onClick={() => handleScheduleSelect(schedule)}
                        sx={{
                          cursor: 'pointer',
                          border:
                            selectedSchedule?.id === schedule.id
                              ? '2px solid #EE7B57'
                              : '1px solid #E0E0E0',
                          bgcolor: selectedSchedule?.id === schedule.id ? '#FFF5F2' : 'white',
                          '&:hover': { boxShadow: 3 },
                        }}>
                        <CardContent>
                          <Typography variant="body1" fontWeight="bold">
                            {moment(schedule.start_time, 'HH:mm:ss').format('h:mm A')} -
                            {moment(schedule.end_time, 'HH:mm:ss').format('h:mm A')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ${schedule.price_per_hour}/hour
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {schedule.slots_available} slots available
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}

          {/* Time Slot Selection */}
          {selectedSchedule && (
            <Box mb={3}>
              <Typography fontWeight="bold" mb={1}>
                Step 3: Select Time Slots (Hourly)
              </Typography>
              {hasExistingBookings && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>Note:</strong> Grayed-out time slots indicate times you have already
                  applied for.
                </Alert>
              )}
              <Grid container spacing={1}>
                {timeSlots.map((slot) => {
                  const isSelected = selectedTimeSlots.find((s) => s.start === slot.start);
                  const isAlreadyApplied = isSlotAlreadyApplied(slot);

                  // Determine hover background color
                  let hoverBgColor = '#F5F5F5';
                  if (isAlreadyApplied) {
                    hoverBgColor = '#E0E0E0';
                  } else if (isSelected) {
                    hoverBgColor = undefined;
                  }

                  const chipComponent = (
                    <Chip
                      label={slot.display}
                      onClick={() => handleTimeSlotToggle(slot)}
                      color={isSelected ? 'primary' : 'default'}
                      variant={isSelected ? 'filled' : 'outlined'}
                      disabled={isAlreadyApplied}
                      sx={{
                        width: '100%',
                        cursor: isAlreadyApplied ? 'not-allowed' : 'pointer',
                        opacity: isAlreadyApplied ? 0.5 : 1,
                        backgroundColor: isAlreadyApplied ? '#E0E0E0' : undefined,
                        '&:hover': {
                          bgcolor: hoverBgColor,
                        },
                        '&.Mui-disabled': {
                          opacity: 0.6,
                          backgroundColor: '#E0E0E0',
                          color: '#9E9E9E',
                        },
                      }}
                    />
                  );

                  return (
                    <Grid item xs={6} sm={4} md={3} key={slot.start}>
                      {isAlreadyApplied ? (
                        <Tooltip title="You have already applied for this time slot" arrow>
                          {chipComponent}
                        </Tooltip>
                      ) : (
                        chipComponent
                      )}
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}

          {/* Summary */}
          {selectedTimeSlots.length > 0 && (
            <Box p={2} bgcolor="#F5F5F5" borderRadius={1}>
              <Typography variant="body2" fontWeight="bold">
                Selected Slots: {selectedTimeSlots.length}
              </Typography>
              <Typography variant="body2">Duration: {selectedTimeSlots.length} hour(s)</Typography>
              <Typography variant="body1" fontWeight="bold" color="#EE7B57" mt={1}>
                Total: ${calculateTotalPrice()} USD
              </Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </CardContent>
      </SimpleCard>

      <WizardButtons
        nextStep={handleNext}
        previousStep={previousStep}
        loading={loading}
        disabled={!selectedDate || !selectedSchedule || selectedTimeSlots.length === 0}
      />
    </div>
  );
}

DateTimeSelection.propTypes = {
  nextStep: PropTypes.func,
  previousStep: PropTypes.func,
};

DateTimeSelection.defaultProps = {
  nextStep: () => {},
  previousStep: () => {},
};
